import type { Curve, SceneControl } from '@anov/3d-core'
import { DoubleSide, Mesh, MeshBasicMaterial, MirroredRepeatWrapping, RepeatWrapping, TextureLoader, TubeGeometry, Vector3, use } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import { RoundLineGeometry } from '../Utils/round-curve'
import type { ISpline, Option } from './types'

class Spline extends ObjectLayer implements ISpline {
  _width: number
  _color: string
  _points: any
  _radius: number
  _textureUrl: string
  _textureSpeed: number
  _close = false
  mesh!: Mesh
  private _disposeCallbacks: Function

  get width(): number {
    return this._width
  }

  set width(newValue: number) {
    this._width = newValue
    // 更新宽度时重新生成管道几何体
    this.updateGeometry()
  }

  get color(): string {
    return this._color
  }

  set color(newValue: string) {
    this._color = newValue
    // 更新颜色时更新材质
    if (this.mesh && this.mesh.material && !this._textureUrl)
      (this.mesh.material as MeshBasicMaterial).color.set(newValue)
  }

  get points(): any {
    return this._points
  }

  set points(value: any) {
    this._points = value
    // 点发生变化时重新初始化几何体
    this.updateGeometry()
  }

  get radius(): number {
    return this._radius
  }

  set radius(value: number) {
    this._radius = value
    // 半径变化时更新几何体
    this.updateGeometry()
  }

  get textureUrl(): string {
    return this._textureUrl
  }

  set textureUrl(value: string) {
    this._textureUrl = value
    // 纹理URL变化时更新材质
    this.updateMaterial()
  }

  get textureSpeed(): number {
    return this._textureSpeed
  }

  set textureSpeed(value: number) {
    this._textureSpeed = value
  }

  get close(): boolean {
    return this._close
  }

  set close(value: boolean) {
    this._close = value
    // 闭合状态变化时更新几何体
    this.updateGeometry()
  }

  constructor(option: Option, sceneControl: SceneControl) {
    super(sceneControl)
    const { width = 2, color = '#fff', points = [], textureUrl, textureSpeed, radius = 1, close = false } = option
    this._width = width
    this._color = color
    this._radius = radius
    this._points = points
    this._close = close
    this._textureUrl = textureUrl
    this._textureSpeed = textureSpeed
    this.init()
  }

  private init() {
    // 初始化时直接调用统一的创建几何体和材质方法
    try {
      // 创建新的曲线和几何体
      const { curve, geometry } = this.createCurveAndGeometry()

      // 创建材质
      const material = this.createMaterial()

      // 创建网格
      this.mesh = new Mesh(geometry, material)

      // 添加到场景
      this.add(this.mesh)
    }
    catch (error) {
      console.error('Failed to create spline:', error)
    }
  }

  // 统一创建曲线和几何体的方法
  private createCurveAndGeometry(): { curve: Curve<Vector3>; geometry: TubeGeometry } {
    if (!this._points || !this._points.length)
      throw new Error('Points array cannot be empty')

    // 创建曲线点
    const groundLinePoints = this._points.map(v => new Vector3(v.position[0], v.position[1], v.position[2]))

    // 创建圆形线几何体
    const groundLine = new RoundLineGeometry(groundLinePoints, {
      radius: this._radius,
      close: this._close,
    })

    // 获取曲线
    const curve = groundLine.curve

    // 创建管道几何体
    const tube = new TubeGeometry(curve, 10000, this._width, 2, this._close)

    return { curve, geometry: tube }
  }

  // 创建材质方法
  private createMaterial(): MeshBasicMaterial {
    if (this._textureUrl) {
      const textLoader = new TextureLoader()
      const texture = textLoader.load(this._textureUrl)
      texture.repeat.set(1, 2)
      texture.wrapS = RepeatWrapping
      texture.wrapT = MirroredRepeatWrapping

      const material = new MeshBasicMaterial({
        map: texture,
        transparent: true,
      })

      // 设置纹理动画
      if (this._textureSpeed) {
        this._disposeCallbacks = use.useframe(this.sceneControl, () => {
          texture.offset.x += this._textureSpeed
        })
      }

      return material
    }
    else {
      return new MeshBasicMaterial({
        color: this._color,
        side: DoubleSide,
      })
    }
  }

  // 清理资源
  private cleanupResources() {
    if (this.mesh) {
      // 从场景中移除
      this.remove(this.mesh)

      // 释放几何体资源
      if (this.mesh.geometry)
        this.mesh.geometry.dispose()

      // 释放材质资源
      if (this.mesh.material) {
        if (Array.isArray(this.mesh.material))
          this.mesh.material.forEach(mat => mat.dispose())
        else
          this.mesh.material.dispose()
      }

      this._disposeCallbacks && this._disposeCallbacks()
    }
  }

  // 更新几何体方法
  private updateGeometry() {
    if (!this._points || !this._points.length)
      return
    // 清理旧资源
    this.cleanupResources()
    this.init()
  }

  // 更新材质方法
  private updateMaterial() {
    if (!this.mesh)
      return

    // 处理旧材质
    if (this.mesh.material) {
      if (Array.isArray(this.mesh.material))
        this.mesh.material.forEach(mat => mat.dispose())
      else
        this.mesh.material.dispose()
    }

    // 创建新材质
    this.mesh.material = this.createMaterial()
  }

  // 销毁
  destroy() {
    this.cleanupResources()
  }
}

export { Spline }